import React, { ReactNode, useCallback, useRef, useState } from 'react';
import './UploadImage.less';
import { ImagePreview } from '../ImagePreview';
import Button from '../Button/Button';
import Icon from '../Icon/Icon';
import { useTranslation } from 'react-i18next';
import { readImageFileAsDataUri, SUPPORTED_READING_IMAGE_ACCEPT } from '../../../helper/ImageFileHelper';
import { MAX_READING_IMAGES } from '../../../constants/ReadingImageLimits';
import { useDispatch } from 'react-redux';
import { notificationActions } from '../../../redux/notification';

interface UploadImageProps {
    config?: unknown;
    images: Array<string>;
    onChange: (images: string[] | undefined) => void;
    text?: string | ReactNode;
    primary?: boolean;
    onDelete?: (index: number) => void;
    deleteDisabled?: boolean;
}

const UploadImage = ({ images, onChange, text = 'Ablesefoto hinzufügen', primary = false, onDelete, deleteDisabled }: UploadImageProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const inputRef = useRef<HTMLInputElement>(null);
    const [isDragActive, setIsDragActive] = useState(false);

    const commitImage = useCallback(
        (imageBase64: string) => {
            onChange([...images, imageBase64]);
        },
        [images, onChange],
    );

    const handleFiles = useCallback(
        async (file: File | undefined) => {
            if (!file) {
                return;
            }

            try {
                const imageBase64 = await readImageFileAsDataUri(file);
                commitImage(imageBase64);
            } catch (err) {
                console.log('Error in UploadImage: ', err);
                dispatch(
                    notificationActions.showErrorNotification(
                        t('jobs.details.notifications.imageFormatUnsupported.title'),
                        t('jobs.details.notifications.imageFormatUnsupported.message'),
                    ),
                );
            }
        },
        [commitImage, dispatch, t],
    );

    const disabled = images.length >= MAX_READING_IMAGES;
    const dragActive = isDragActive;
    const iconPath = dragActive ? Icon.Path.mdiImageMove : Icon.Path.mdiCamera;

    return (
        <div className="upload-image">
            <Button
                primary={dragActive ? true : primary}
                ghost={!dragActive && !primary}
                disabled={disabled}
                block={true}
                className={dragActive ? 'upload-image--drag-active border-dashed border-2 border-primary ring-2 ring-primary shadow-md' : undefined}
                onClick={() => {
                    inputRef.current?.click();
                }}
                onDragEnter={(event) => {
                    event.preventDefault();
                    setIsDragActive(true);
                }}
                onDragOver={(event) => {
                    event.preventDefault();
                    event.dataTransfer.dropEffect = 'copy';
                    setIsDragActive(true);
                }}
                onDragLeave={(event) => {
                    event.preventDefault();

                    if (event.currentTarget.contains(event.relatedTarget as Node)) {
                        return;
                    }

                    setIsDragActive(false);
                }}
                onDrop={async (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setIsDragActive(false);

                    await handleFiles(event.dataTransfer.files?.[0]);
                }}
            >
                <Icon path={iconPath} /> {dragActive ? t('jobs.details.buttons.dropImage') : text}
            </Button>
            <input
                ref={inputRef}
                type="file"
                accept={SUPPORTED_READING_IMAGE_ACCEPT}
                hidden
                onChange={async (event) => {
                    const input = event.currentTarget;

                    try {
                        await handleFiles(input.files?.[0]);
                    } finally {
                        input.value = '';
                    }
                }}
            />
            <ImagePreview data={images} onDelete={onDelete} disabled={deleteDisabled} />
        </div>
    );
};

export default UploadImage;
